import * as THREE from 'three';
import { getFullUrl } from '../config';
import { UniversalPlacementSystem } from './UniversalPlacementSystem';

/**
 * Handles room creation, management, and 3D model loading
 */
export class RoomManager {
  constructor(scene, loadingManager = null, multiRoomManager = null) {
    this.scene = scene;
    this.loadingManager = loadingManager;
    this.multiRoomManager = multiRoomManager;
    this.floor = null;
    this.floorMeshes = [];
    this.customWalls = null;
    this.placementSystem = null;
    this.currentRoomContainer = null; // Will be set during room creation
  }

  /**
   * Create a room (either custom 3D model or procedural)
   * @param {Object} showroom - Showroom configuration
   * @param {THREE.Object3D} targetContainer - Container for room objects (optional, uses scene if not provided)
   */
  async createRoom(showroom, targetContainer = null) {
    const { width, height, depth } = showroom;

    // Set the target container for this room creation
    this.currentRoomContainer = targetContainer || this.scene;
    
    // Only clear room if using scene directly (backward compatibility)
    // When using containers, each room has its own container so no clearing needed
    if (!targetContainer) {
      this.clearRoom();
    }
    
    // Creating room silently for background loading
    
    // Check if this is a custom 3D model showroom
    if (showroom.modelType === 'custom' && showroom.modelUrl) {
      // Loading custom 3D model room silently
      try {
        await this.loadCustomRoom(showroom);
      } catch (error) {
        // Custom room failed - don't fall back, let the error propagate
        console.error(`❌ Custom room "${showroom.name}" failed to load:`, error.message);
        throw error; // This will cause room validation to fail and show proper error to user
      }
    } else {
      // Use existing procedural generation
      console.log('🏗️ Creating procedural room...');
      this.createProceduralRoom(showroom);
    }
    
    // Validate room creation was successful
    const hasFloor = this.getFloor() || this.getFloorMeshes().length > 0;
    const hasValidScene = this.scene.children.length > 0;
    
    // Room validation complete
    
    if (!hasFloor || !hasValidScene) {
      console.warn('⚠️ Room creation validation failed - room may not be fully loaded');
      return false;
    }
    
    // Room creation completed
    return true;
  }

  /**
   * Load a custom 3D room model
   */
  async loadCustomRoom(showroom) {
    // Loading custom 3D room model silently
    
    // Track room model loading
    const loadingOperationId = this.loadingManager?.startLoading(`Room model: ${showroom.name}`);
    
    try {
      // Load custom .glb/.gltf model with proper await
      // Fetching 3D model silently
      const gltf = await this.load3DModel(showroom.modelUrl);
      // 3D model loaded successfully
      
      const model = gltf.scene;

      // Position the model
      model.position.set(0, 0, 0);
      model.scale.set(1, 1, 1);
      this.currentRoomContainer.add(model);
      // 3D model added to scene

      // Parse modelData for interactive surfaces
      const modelData = showroom.modelData ? JSON.parse(showroom.modelData) : null;
      if (modelData) {
        this.setupInteractiveSurfaces(model, modelData);
        this.setupCustomRoomLighting(modelData.bounds);
        // Interactive surfaces and lighting configured
      } else {
        // Fallback lighting if no modelData available
        this.setupDefaultLighting();
        console.log('⚠️ No modelData found, using default lighting');
      }
      
      // Custom room loading complete
    } catch (error) {
      console.error('❌ Failed to load custom room:', error);
      
      // For custom rooms, NEVER fall back to procedural - that's not what the user expects
      // Instead, retry with mobile optimizations or show an error
      
      if (error.message && error.message.includes('out of memory')) {
        console.error('💥 Memory issue detected - custom room too large for this device');
        throw new Error('Custom 3D room requires more memory than available on this device. Try closing other apps or use a device with more RAM.');
      } else {
        console.error('❌ Custom room failed to load - network or file issue');
        throw new Error('Failed to load custom 3D room. Check your internet connection and try again.');
      }
    } finally {
      // Always finish the loading operation
      if (loadingOperationId) {
        this.loadingManager?.finishLoading(loadingOperationId);
      }
    }
  }

  /**
   * Create a procedural room with walls, floor, ceiling
   */
  createProceduralRoom(showroom) {
    const { width, height, depth } = showroom;

    // Create loading manager for coordinated texture loading
    const loadingManager = new THREE.LoadingManager();
    const textureLoader = new THREE.TextureLoader(loadingManager);

    // Floor
    const floorTexture = textureLoader.load(
      'https://i.imgur.com/onN1zfo.jpeg',
      // onLoad callback
      () => console.log('Floor texture loaded successfully'),
      // onProgress callback
      undefined,
      // onError callback
      (error) => {
        console.warn('Failed to load floor texture, using fallback color');
        this.floor.material.map = null;
        this.floor.material.color.setHex(0x888888);
        this.floor.material.needsUpdate = true;
      }
    );
    floorTexture.wrapS = floorTexture.wrapT = THREE.RepeatWrapping;
    floorTexture.repeat.set(8, 8);

    this.floor = new THREE.Mesh(
      new THREE.PlaneGeometry(width, depth),
      new THREE.MeshStandardMaterial({
        map: floorTexture,
        roughness: 0.8,
        metalness: 0.2
      })
    );
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.name = "floor";
    this.floor.receiveShadow = true;
    this.currentRoomContainer.add(this.floor);

    // Walls
    const wallTexture = textureLoader.load(
      'https://i.imgur.com/V9wtIRM.jpeg',
      // onLoad callback
      () => console.log('Wall texture loaded successfully'),
      // onProgress callback
      undefined,
      // onError callback
      (error) => {
        console.warn('Failed to load wall texture, using fallback color');
        // Update all walls with fallback color
        this.scene.traverse((child) => {
          if (child.isMesh && child !== this.floor && child.material && child.material.map === wallTexture) {
            child.material.map = null;
            child.material.color.setHex(0xcccccc);
            child.material.needsUpdate = true;
          }
        });
      }
    );
    wallTexture.wrapS = wallTexture.wrapT = THREE.RepeatWrapping;
    wallTexture.repeat.set(10, 2);
    const wallMaterial = new THREE.MeshStandardMaterial({
      map: wallTexture,
      roughness: 0.9
    });

    // Back Wall
    const backWall = new THREE.Mesh(new THREE.PlaneGeometry(width, height), wallMaterial);
    backWall.position.set(0, height / 2, -depth / 2);
    backWall.receiveShadow = true;
    this.currentRoomContainer.add(backWall);

    // Left Wall
    const leftWall = new THREE.Mesh(new THREE.PlaneGeometry(depth, height), wallMaterial);
    leftWall.position.set(-width / 2, height / 2, 0);
    leftWall.rotation.y = Math.PI / 2;
    leftWall.receiveShadow = true;
    this.currentRoomContainer.add(leftWall);

    // Right Wall
    const rightWall = new THREE.Mesh(new THREE.PlaneGeometry(depth, height), wallMaterial);
    rightWall.position.set(width / 2, height / 2, 0);
    rightWall.rotation.y = -Math.PI / 2;
    rightWall.receiveShadow = true;
    this.currentRoomContainer.add(rightWall);

    // Front Wall
    const frontWall = new THREE.Mesh(new THREE.PlaneGeometry(width, height), wallMaterial);
    frontWall.position.set(0, height / 2, depth / 2);
    frontWall.rotation.y = Math.PI;
    frontWall.receiveShadow = true;
    this.currentRoomContainer.add(frontWall);

    // Ceiling
    const ceiling = new THREE.Mesh(
      new THREE.PlaneGeometry(width, depth),
      new THREE.MeshStandardMaterial({ color: '#ffffff' })
    );
    ceiling.position.y = height;
    ceiling.rotation.x = Math.PI / 2;
    ceiling.receiveShadow = true;
    this.currentRoomContainer.add(ceiling);

    // Bright lighting for artwork visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 2.2); // Enhanced for better artwork visibility
    this.currentRoomContainer.add(ambientLight);

    // Only add directional light on non-mobile devices
    if (!('ontouchstart' in window)) {
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0); // Increased for bright artwork
      directionalLight.position.set(5, 10, 5);
      directionalLight.castShadow = false;
      this.currentRoomContainer.add(directionalLight);
    }
  }


  /**
   * Clear existing room objects
   */
  clearRoom() {
    console.log('🧹 Clearing room - removing all scene objects');
    
    // Aggressive memory cleanup for all devices (especially mobile)
    console.log('🧹 Starting aggressive memory cleanup');
    
    // Clear all THREE.js caches - this is crucial for blob URL cleanup
    THREE.Cache.clear();
    
    // Force WebGL context cleanup if available
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      console.log('📱 Mobile detected - extra aggressive cleanup');
      
      // Force garbage collection if available (Chrome with --js-flags=--expose-gc)
      if (typeof window !== 'undefined' && window.gc) {
        console.log('🗑️ Forcing garbage collection');
        window.gc();
      }
      
      // Add delay for mobile garbage collection
      setTimeout(() => {
        if (typeof window !== 'undefined' && window.gc) {
          window.gc();
        }
      }, 100);
    }
    
    // Remove ALL objects from scene except for cameras (which aren't in scene anyway)
    // This ensures complete cleanup between room switches
    const objectsToRemove = [];
    
    // Get direct children of scene (not traverse, to avoid issues with nested objects)
    this.scene.children.slice().forEach(child => {
      // Keep only essential Three.js system objects that shouldn't be removed
      const keepObject = child.type === 'PerspectiveCamera' || 
                        child.type === 'OrthographicCamera';
      
      if (!keepObject) {
        objectsToRemove.push(child);
      }
    });

    console.log(`🗑️ Removing ${objectsToRemove.length} scene objects`);
    
    // Remove and dispose objects
    objectsToRemove.forEach(obj => {
      this.scene.remove(obj); // Remove from scene first
      this.disposeObject(obj); // Then dispose
    });

    // Reset room state
    this.interactiveObjects = [];
    this.floor = null;
    this.floorMeshes = [];
    this.customWalls = null;
    this.placementSystem = null;
    
    console.log('✅ Room clearing complete');
  }

  /**
   * Dispose of a 3D object properly with aggressive memory cleanup
   */
  disposeObject(obj) {
    if (!obj) return;

    console.log('🗑️ Disposing object:', obj.name || obj.type);

    // Recursively dispose children first (important for GLTF models)
    if (obj.children && obj.children.length > 0) {
      // Create a copy of children array since we're modifying it
      const children = [...obj.children];
      children.forEach(child => this.disposeObject(child));
    }

    // Dispose geometry
    if (obj.geometry) {
      obj.geometry.dispose();
      obj.geometry = null;
    }

    // Dispose materials with enhanced cleanup (handle both single material and array)
    if (obj.material) {
      if (Array.isArray(obj.material)) {
        obj.material.forEach(material => this.disposeMaterial(material));
      } else {
        this.disposeMaterial(obj.material);
      }
      obj.material = null;
    }

    // Remove from parent
    if (obj.parent) {
      obj.parent.remove(obj);
    }

    // Clear userData and references
    if (obj.userData) {
      obj.userData = {};
    }
    
    // Clear any Three.js internal references
    obj.removeFromParent && obj.removeFromParent();
    obj.clear && obj.clear();
  }

  /**
   * Dispose of a material properly with enhanced texture cleanup
   */
  disposeMaterial(material) {
    if (!material) return;

    console.log('🗑️ Disposing material:', material.type);

    // Enhanced texture disposal - dispose all texture properties
    const textureProperties = [
      'map', 'lightMap', 'bumpMap', 'normalMap', 'displacementMap', 
      'roughnessMap', 'metalnessMap', 'alphaMap', 'envMap', 'emissiveMap',
      'aoMap', 'specularMap', 'glossinessMap', 'clearcoatMap', 'clearcoatNormalMap',
      'clearcoatRoughnessMap', 'transmissionMap', 'thicknessMap'
    ];

    textureProperties.forEach(prop => {
      const texture = material[prop];
      if (texture && texture.isTexture) {
        console.log(`🗑️ Disposing texture: ${prop}`);
        
        // Dispose the texture
        texture.dispose();
        
        // Clear the reference
        material[prop] = null;
        
        // If it's a blob URL, revoke it to free memory immediately
        if (texture.image && texture.image.src && texture.image.src.startsWith('blob:')) {
          console.log('🗑️ Revoking blob URL:', texture.image.src);
          URL.revokeObjectURL(texture.image.src);
        }
      }
    });

    // Dispose the material itself
    material.dispose();
    
    // Clear material properties
    material.map = null;
    material.normalMap = null;
    material.roughnessMap = null;
    material.metalnessMap = null;
  }

  /**
   * Load a 3D model (GLTF/GLB)
   */
  async load3DModel(url) {
    try {
      return await this.loadGLTFModel(url);
    } catch (error) {
      console.error('Error loading 3D model:', error);
      throw error;
    }
  }

  /**
   * Generic GLTF loader with mobile memory optimization
   */
  async loadGLTFModel(url) {
    // Dynamic import of GLTFLoader to avoid build issues
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
    const loader = new GLTFLoader();

    // Loading 3D model
    
    // Check if we're on mobile and potentially low on memory
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isLowMemory = navigator.deviceMemory && navigator.deviceMemory <= 4; // 4GB or less
    
    // Detect incognito mode
    const isIncognito = await this.detectIncognitoMode();
    
    if (isMobile || isLowMemory) {
      console.log('📱 Mobile device detected - enabling memory optimizations');
    }
    
    if (isIncognito) {
      console.log('🕵️ Incognito mode detected - patching TextureLoader to avoid blob URLs');
      this.patchTextureLoaderForIncognito();
    }

    // Add timeout to prevent hanging loads with proper cleanup
    let timeoutId = null;
    
    const loadPromise = new Promise((resolve, reject) => {
      loader.load(
        getFullUrl(url),
        (gltf) => {
          // Clear timeout on successful load
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          // GLTF loaded
          // Validate that the model has content
          if (!gltf.scene || !gltf.scene.children || gltf.scene.children.length === 0) {
            console.warn('⚠️ GLTF loaded but scene is empty');
            reject(new Error('Empty GLTF scene'));
            return;
          }
          
          // Apply mobile memory optimizations
          if (isMobile || isLowMemory) {
            this.optimizeModelForMobile(gltf.scene);
          }
          
          resolve(gltf);
        },
        (progress) => {
          // Loading progress tracked silently
        },
        (error) => {
          // Clear timeout on error
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          
          console.error('❌ GLTF load error:', error);
          
          // Check if it's a memory-related error
          if (error.message && (error.message.includes('OUT_OF_MEMORY') || error.message.includes('BLOB_OUT_OF_MEMORY'))) {
            console.error('💥 Out of memory error detected - mobile device may need memory optimization');
            
            // Try to free some memory
            THREE.Cache.clear();
            
            // Force garbage collection if available (Chrome)
            if (window.gc) {
              window.gc();
            }
            
            reject(new Error('Mobile device out of memory - try closing other apps'));
          } else {
            reject(error);
          }
        }
      );
    });

    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        console.error('❌ GLTF load timeout after 2 minutes');
        reject(new Error('Model load timeout - check network connection'));
      }, 120000); // 2 minute timeout for rooms
    });

    try {
      return await Promise.race([loadPromise, timeoutPromise]);
    } finally {
      // Ensure timeout is always cleared
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }
  }

  /**
   * Optimize 3D model for mobile devices with rendering optimizations (NOT texture downscaling)
   */
  optimizeModelForMobile(scene) {
    console.log('📱 Applying mobile rendering optimizations...');
    let materialsOptimized = 0;
    
    scene.traverse((child) => {
      if (child.isMesh && child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        
        materials.forEach(material => {
          // Mobile-friendly rendering optimizations WITHOUT changing textures
          const textureProperties = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'bumpMap'];
          
          textureProperties.forEach(prop => {
            if (material[prop] && material[prop].isTexture) {
              const texture = material[prop];
              
              // Optimize texture filtering for mobile performance (keeps original resolution)
              texture.magFilter = THREE.LinearFilter;
              texture.minFilter = THREE.LinearFilter; // Skip expensive mipmaps
              texture.generateMipmaps = false; // Disable mipmap generation to save memory
              
              // DO NOT change texture format - this breaks sRGB textures
              // texture.format should be left as-is (RGBAFormat for sRGB)
              texture.flipY = false; // Disable Y-flip to save processing
            }
          });
          
          // Disable expensive material features on mobile
          if (material.isMeshStandardMaterial) {
            // Simplify material for mobile performance
            material.roughness = Math.max(0.3, material.roughness || 0.5); // Avoid mirror-like surfaces
            material.metalness = Math.min(0.7, material.metalness || 0.0); // Reduce metallic reflections
          }
          
          // Force material update
          material.needsUpdate = true;
          materialsOptimized++;
        });
        
        // Disable shadows for mobile performance
        child.castShadow = false;
        child.receiveShadow = false;
      }
    });
    
    console.log(`📱 Mobile optimization complete - ${materialsOptimized} materials optimized (textures preserved)`);
  }

  /**
   * Setup interactive surfaces for custom rooms
   */
  setupInteractiveSurfaces(model, modelData) {
    this.floorMeshes = []; // Array to store all floor meshes

    // Find floor meshes for teleportation
    modelData.floors.forEach(floorData => {
      const floorMesh = model.getObjectByName(floorData.id);
      if (floorMesh) {
        console.log(`Found floor mesh: ${floorData.id}`, floorMesh);
        floorMesh.name = "floor"; // Mark for raycasting
        floorMesh.userData.floorId = floorData.id; // Store floor ID for identification
        this.floorMeshes.push(floorMesh);
        if (!this.floor) {
          this.floor = floorMesh; // Keep first one as main floor for backward compatibility
        }
      } else {
        console.warn(`Floor mesh not found: ${floorData.id}`);
      }
    });

    // If no specific floor found, try to find any floor mesh
    if (this.floorMeshes.length === 0) {
      model.traverse((child) => {
        if (child.isMesh && child.name.toLowerCase().includes('floor')) {
          child.name = "floor";
          this.floorMeshes.push(child);
          if (!this.floor) {
            this.floor = child;
          }
        }
      });
    }

    console.log(`Found ${this.floorMeshes.length} floor meshes for teleportation`);

    // Store wall surfaces for placement validation
    this.customWalls = modelData.walls;

    // Initialize placement system with surface data
    if (modelData.surfaces) {
      this.placementSystem = new UniversalPlacementSystem(modelData.surfaces);
      console.log('Universal Placement System initialized with', modelData.surfaces.length, 'surfaces');
    }
  }

  /**
   * Setup lighting for custom rooms
   */
  setupCustomRoomLighting(bounds) {
    // Calculate room center and size
    const center = {
      x: (bounds.min.x + bounds.max.x) / 2,
      y: (bounds.min.y + bounds.max.y) / 2,
      z: (bounds.min.z + bounds.max.z) / 2
    };

    const size = {
      x: bounds.max.x - bounds.min.x,
      y: bounds.max.y - bounds.min.y,
      z: bounds.max.z - bounds.min.z
    };

    // Bright ambient light for artwork visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 2.2); // Enhanced for better artwork visibility
    this.currentRoomContainer.add(ambientLight);

    // Add directional lights based on room size
    if (!('ontouchstart' in window)) {
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0); // Increased for artwork brightness
      directionalLight.position.set(
        center.x + size.x * 0.5,
        bounds.max.y + 2,
        center.z + size.z * 0.5
      );
      directionalLight.castShadow = false;
      this.currentRoomContainer.add(directionalLight);
    }
  }

  /**
   * Fallback lighting setup
   */
  setupDefaultLighting() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.8); // Match increased procedural room lighting
    this.currentRoomContainer.add(ambientLight);

    if (!('ontouchstart' in window)) {
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0); // Match increased procedural room lighting
      directionalLight.position.set(5, 10, 5);
      directionalLight.castShadow = false;
      this.currentRoomContainer.add(directionalLight);
    }
  }

  /**
   * Validate placement against room boundaries
   */
  validatePlacement(item, placement) {
    if (this.customWalls) {
      // Use modelData for custom room validation
      return this.validateAgainstCustomWalls(item, placement);
    } else {
      // Use existing procedural validation (if needed)
      return this.validateAgainstProceduralRoom(item, placement);
    }
  }

  /**
   * Validate placement against custom walls from modelData
   */
  validateAgainstCustomWalls(item, placement) {
    const { positionX, positionY, positionZ } = placement;

    // For paintings (items without 3D models), check if position is near any placeable wall surface
    if (!item.modelUrl) {
      return this.customWalls.some(wall => {
        if (!wall.placeable) return false;

        // Calculate distance from placement to wall center
        const distanceToWall = Math.sqrt(
          Math.pow(positionX - wall.position.x, 2) +
          Math.pow(positionY - wall.position.y, 2) +
          Math.pow(positionZ - wall.position.z, 2)
        );

        // Allow placement if within reasonable distance of wall
        const maxDistance = Math.max(wall.dimensions.width, wall.dimensions.height) / 2 + 1;
        return distanceToWall <= maxDistance;
      });
    }

    // For 3D items, just check they're within the room bounds
    if (item.modelUrl) {
      // Check if floor position exists and is accessible
      return this.customWalls.length > 0; // If we have walls, we have a valid room
    }

    return true; // Allow by default
  }

  /**
   * Validate placement against procedural room (existing system)
   */
  validateAgainstProceduralRoom(item, placement) {
    // For procedural rooms, we don't need strict validation
    // as the admin interface already ensures valid placement
    return true;
  }

  // Getters for accessing room state
  getFloor() { return this.floor; }
  getFloorMeshes() { return this.floorMeshes; }
  getCustomWalls() { return this.customWalls; }
  getPlacementSystem() { return this.placementSystem; }
  
  /**
   * Get the center position of the main floor for camera spawning
   */
  getFloorCenter(roomId = null) {
    // If roomId provided, get floor from specific room container
    if (roomId && this.multiRoomManager) {
      const roomContainer = this.multiRoomManager.getRoomContainer(roomId);
      if (roomContainer) {
        let roomFloor = null;
        roomContainer.traverse((child) => {
          if (child.isMesh && (child.name === 'floor' || child.name.includes('floor')) && !roomFloor) {
            roomFloor = child;
          }
        });
        
        if (roomFloor) {
          const box = new THREE.Box3().setFromObject(roomFloor);
          const center = box.getCenter(new THREE.Vector3());
          return { x: center.x, y: 0, z: center.z };
        }
      }
    }
    
    // For custom rooms, try to find floor_main first
    if (this.floorMeshes.length > 0) {
      // Look for main floor specifically
      const mainFloor = this.floorMeshes.find(mesh => 
        mesh.userData.floorId === 'floor_main' || 
        mesh.name.toLowerCase().includes('main')
      );
      
      if (mainFloor) {
        console.log('📍 Using floor_main for center calculation');
        const box = new THREE.Box3().setFromObject(mainFloor);
        const center = box.getCenter(new THREE.Vector3());
        return { x: center.x, y: 0, z: center.z };
      }
      
      // Fallback to first floor mesh
      console.log('📍 Using first available floor for center calculation');
      const box = new THREE.Box3().setFromObject(this.floorMeshes[0]);
      const center = box.getCenter(new THREE.Vector3());
      return { x: center.x, y: 0, z: center.z };
    }
    
    // For procedural rooms, calculate center from floor mesh
    if (this.floor) {
      console.log('📍 Using procedural floor for center calculation');
      const box = new THREE.Box3().setFromObject(this.floor);
      const center = box.getCenter(new THREE.Vector3());
      return { x: center.x, y: 0, z: center.z };
    }
    
    // Ultimate fallback - origin
    console.warn('📍 No floor found, using origin as center');
    return { x: 0, y: 0, z: 0 };
  }

  /**
   * Detect incognito/private browsing mode
   */
  async detectIncognitoMode() {
    try {
      // Method 1: Storage quota (most reliable)
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        // Incognito typically has much lower quota (< 100MB vs several GB)
        return estimate.quota < 120000000; // Less than ~120MB suggests incognito
      }
      
      // Method 2: IndexedDB detection (fallback)
      return new Promise((resolve) => {
        const db = indexedDB.open('test');
        db.onerror = () => resolve(true); // Likely incognito if fails
        db.onsuccess = () => {
          resolve(false);
          indexedDB.deleteDatabase('test');
        };
      });
    } catch (e) {
      // If detection fails, assume worst case (incognito)
      return true;
    }
  }

  /**
   * Patch TextureLoader to avoid blob URLs in incognito mode
   */
  patchTextureLoaderForIncognito() {
    // Store original load method
    const originalLoad = THREE.TextureLoader.prototype.load;
    
    // Override TextureLoader to handle incognito mode
    THREE.TextureLoader.prototype.load = function(url, onLoad, onProgress, onError) {
      // Check if this is a blob URL that might fail in incognito
      if (url && url.startsWith && url.startsWith('blob:')) {
        console.log('🕵️ Intercepting blob URL in incognito mode:', url.substring(0, 50) + '...');
        
        // Try to convert blob URL to data URL to avoid memory issues
        fetch(url)
          .then(response => response.blob())
          .then(blob => {
            const reader = new FileReader();
            reader.onload = () => {
              console.log('🕵️ Converted blob to data URL for incognito mode');
              // Use data URL instead of blob URL
              originalLoad.call(this, reader.result, onLoad, onProgress, onError);
            };
            reader.onerror = () => {
              console.warn('🕵️ Failed to convert blob URL, creating fallback texture');
              // Create a simple fallback texture
              const canvas = document.createElement('canvas');
              canvas.width = canvas.height = 256;
              const ctx = canvas.getContext('2d');
              ctx.fillStyle = '#cccccc';
              ctx.fillRect(0, 0, 256, 256);
              const fallbackUrl = canvas.toDataURL();
              originalLoad.call(this, fallbackUrl, onLoad, onProgress, onError);
            };
            reader.readAsDataURL(blob);
          })
          .catch(error => {
            console.error('🕵️ Blob URL fetch failed in incognito mode:', error);
            if (onError) onError(error);
          });
        return;
      }
      
      // For non-blob URLs, use original method
      return originalLoad.call(this, url, onLoad, onProgress, onError);
    };
    
    console.log('🕵️ TextureLoader patched for incognito mode');
  }
}